<script setup lang="ts">
import type { StatusOption } from "@/entities/evaluation/api";

import { getStatusOptionImageUrl } from "@/entities/evaluation/lib";
import { LoaderIcon } from "@/shared/ui";

const { options, selectedId, loadingId } = defineProps<{
    options: StatusOption[];
    /** Вариант, на который пользователь уже ответил */
    selectedId: string | null;
    /** Вариант, ответ на который сейчас отправляется */
    loadingId: string | null;
}>();

const emit = defineEmits<{ select: [option: StatusOption] }>();
</script>

<template>
    <div class="options" :class="{ options_loading: loadingId }" role="radiogroup">
        <button
            v-for="option in options"
            :key="option.cid"
            type="button"
            role="radio"
            class="option"
            :class="{ option_selected: option.cid === (loadingId ?? selectedId) }"
            :aria-checked="option.cid === (loadingId ?? selectedId)"
            :disabled="Boolean(loadingId)"
            @click="emit('select', option)"
        >
            <img class="option__image" :src="getStatusOptionImageUrl(option)" alt="" />
            <span class="option__body">
                <span class="option__title">{{ option.cname }}</span>
                <span class="option__text">{{ option.description }}</span>
            </span>
            <LoaderIcon v-if="option.cid === loadingId" class="option__loader" />
        </button>
    </div>
</template>

<style scoped>
.options {
    display: flex;
    flex-direction: column;
    gap: 12px;
    user-select: none;

    @media screen and (max-width: 767px) {
        gap: 8px;
    }
}

.option {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px 16px;
    border: 2px solid #d3dce9;
    border-radius: 6px;
    background: none;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
    transition:
        border-color 150ms ease,
        opacity 150ms ease;

    &:hover {
        border-color: var(--custom-color-main-hover, var(--custom-color-main));
    }

    @media screen and (max-width: 767px) {
        gap: 8px;
        padding: 8px;
    }
}

.option_selected {
    border-color: var(--custom-color-main);
}

/* While the answer is being sent, only the chosen option stays bright */
.options_loading .option {
    cursor: default;

    &:not(.option_selected) {
        opacity: 0.5;
    }
}

.option__image {
    flex: 0 0 72px;
    width: 72px;

    @media screen and (max-width: 767px) {
        flex-basis: 64px;
        width: 64px;
    }
}

.option__body {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 4px;

    @media screen and (max-width: 767px) {
        gap: 2px;
    }
}

.option__title {
    font-size: calc(var(--custom-font-size) * 1.143);
    font-weight: 700;
    line-height: 1.25;

    @media screen and (max-width: 767px) {
        font-size: var(--custom-font-size);
        line-height: 1.286;
    }
}

.option__text {
    font-size: var(--custom-font-size);
    line-height: 1.429;

    @media screen and (max-width: 767px) {
        font-size: calc(var(--custom-font-size) * 0.857);
        line-height: 1.25;
    }
}

.option__loader {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 14px;
    height: 14px;
}
</style>
